"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Clock, Droplets, PhoneCall, Pipette, ShieldCheck, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  bullet1: string;
  bullet2: string;
  bullet3: string;
  linkPath: string;
  ctaText: string;
  isEmergency: boolean;
}
const servicesData: ServiceItem[] = [{
  id: "pipe-repair",
  title: "Pipe Repair & Leak Detection",
  badge: "Same-Day Fixes",
  description: "Rapid pinpointing and permanent repairs for burst pipes, noisy water hammers, pinhole leaks, and frozen lines in Jonesboro homes.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png-wvc-sizes",
  imageAlt: "Plumber repairing residential copper water pipes under sink",
  bullet1: "Non-invasive electronic leak detection",
  bullet2: "Copper, PEX, and PVC pipe replacements",
  bullet3: "Preventative pressure testing & valve checks",
  linkPath: "/services/",
  ctaText: "Explore Pipe Repairs",
  isEmergency: false
}, {
  id: "sewer-repair",
  title: "Sewer Line Repair & Clearing",
  badge: "Heavy-Duty Equipment",
  description: "Complete diagnostic camera inspections and targeted repairs for tree root intrusions, collapsed pipes, and severe main line blockages.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_service.png-wvc-sizes",
  imageAlt: "Technician inspecting underground sewer line with specialized camera monitor",
  bullet1: "HD video camera line inspections",
  bullet2: "Hydro jetting & root extraction",
  bullet3: "Trenchless repair options available",
  linkPath: "/services/",
  ctaText: "Explore Sewer Services",
  isEmergency: false
}, {
  id: "emergency-plumbing",
  title: "24/7 Emergency Plumbing",
  badge: "Immediate Dispatch",
  description: "Urgent response teams ready day and night for catastrophic leaks, flooding, raw sewage backflow, and complete water supply failures.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service.png-wvc-sizes",
  imageAlt: "Emergency plumbing vehicle arriving at residential driveway with equipment",
  bullet1: "Average local response under 45 minutes",
  bullet2: "Fully stocked service vans on call 24/7",
  bullet3: "Clear, upfront emergency rates without surprises",
  linkPath: "/contact/",
  ctaText: "Request Emergency Help",
  isEmergency: true
}];
export default function ServicesGrid() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="coreservicesgrid" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-3 font-semibold px-3 py-1">
            <ShieldCheck className="size-3.5 text-chart-1" />
            Core Local Services
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Fast, Reliable Plumbing Solutions in Jonesboro
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Whether you are dealing with a midnight pipe burst or require mainline sewer maintenance, our licensed technicians deliver prompt, durable repairs across Jonesboro and surrounding areas.
          </p>
        </div>

        {/* 3-Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {servicesData.map((service, index) => <div key={service.id} data-index={index} className="h-full">
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.45,
            delay: index * 0.1
          }} className="h-full flex flex-col">
                <Card className={`h-full flex flex-col justify-between overflow-hidden transition-all duration-200 border-2 ${service.isEmergency ? "border-accent shadow-md hover:shadow-lg" : "border-border hover:border-primary/40 shadow-xs hover:shadow-md"}`}>
                  <div>
                    {/* Visual Header / Image */}
                    <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                      <img src={service.imageSrc} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-3 left-3">
                        <Badge variant={service.isEmergency ? "default" : "secondary"} className={`font-semibold shadow-xs ${service.isEmergency ? "bg-accent text-accent-foreground hover:bg-accent" : "bg-background/90 text-foreground backdrop-blur-sm"}`}>
                          {service.isEmergency ? <Clock className="size-3 text-accent-foreground" /> : <Droplets className="size-3 text-chart-1" />}
                          {service.badge}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-3">
                      <h3 className="font-default text-xl sm:text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1.5">
                        {service.description}
                      </p>
                    </CardHeader>

                    <CardContent className="pt-2 pb-6">
                      <div className="space-y-2.5 pt-2 border-t border-border">
                        <div className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                          <span>{service.bullet1}</span>
                        </div>
                        <div className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                          <span>{service.bullet2}</span>
                        </div>
                        <div className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                          <span>{service.bullet3}</span>
                        </div>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-0 pb-6 flex flex-col gap-3">
                    <Button asChild variant={service.isEmergency ? "default" : "outline"} className={`w-full justify-between h-11 text-base ${service.isEmergency ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border-border text-foreground hover:bg-secondary"}`}>
                      <Link to={service.linkPath}>
                        <span>{service.ctaText}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Bottom Quick Call Support Strip */}
        <div className="mt-12 sm:mt-16 bg-secondary/80 rounded-2xl p-6 sm:p-8 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-center md:text-left">
            <div className="p-3 bg-primary text-primary-foreground rounded-xl shrink-0 hidden sm:flex items-center justify-center">
              <PhoneCall className="size-6" />
            </div>
            <div>
              <h4 className="font-default text-lg sm:text-xl font-bold text-foreground">
                Need an immediate assessment on your plumbing?
              </h4>
              <p className="text-muted-foreground text-sm mt-0.5">
                Our technicians are on standby throughout Jonesboro, GA for emergency calls.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 w-full md:w-auto">
            <Button asChild variant="default" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto font-semibold">
              <Link to="/contact/">
                <Wrench className="size-4 mr-2" />
                Schedule Service
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-secondary w-full sm:w-auto font-medium">
              <Link to="/problems/">
                View Common Problems
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}